import { useRegistryStore } from '@/stores/registry.store'
import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { codeNameDisplayExpr, customerDisplayExpr } from '@/composables/label'

export const useLookupModelMap = () => {
  const registryStore = useRegistryStore()
  const garageStore = useGarageStore()
  const websiteStore = useWebSiteStore()
  const portalStore = usePortalStore()
  const accountingStore = useAccountingStore()

  const lookupModels = {
    'portal.country': { getItems: () => websiteStore.countries, loadAsync: websiteStore.fetchCountriesAsync, editor: 'country' },
    'portal.state': { getItems: () => websiteStore.states, loadAsync: websiteStore.fetchStatesAsync },
    'portal.city': { editor: 'city' },
    'portal.subjecttype': { getItems: () => registryStore.subjectTypes, loadAsync: registryStore.fetchSubjectTypesAsync },
    'portal.identitydocumenttype': { getItems: () => registryStore.identityDocumentTypes, loadAsync: registryStore.fetchIdentityDocumentTypesAsync },
    'portal.identityauthority': { getItems: () => registryStore.identityDocumentAuthorities, loadAsync: registryStore.fetchIdentityDocumentAuthoritiesAsync },
    'portal.jobtype': { getItems: () => registryStore.jobTypes, loadAsync: registryStore.fetchJobTypesAsync },
    'portal.paymenttype': { getItems: () => websiteStore.paymentTypes, loadAsync: websiteStore.fetchPaymentTypesAsync },
    'portal.buyertype': { getItems: () => registryStore.buyerTypes, loadAsync: registryStore.fetchBuyerTypesAsync },
    'portal.channeltype': { getItems: () => registryStore.channelTypes, loadAsync: registryStore.fetchChannelTypesAsync },
    'portal.channel': { getItems: () => registryStore.channels, loadAsync: registryStore.fetchChannelsAsync },
    'portal.discountlist': { getItems: () => registryStore.discountLists, loadAsync: registryStore.fetchDiscountListsAsync },
    'portal.customertype': { getItems: () => registryStore.customerTypes, loadAsync: registryStore.fetchCustomerTypesAsync },
    'portal.vattype': { getItems: () => websiteStore.getLocalizedVatTypes(), loadAsync: websiteStore.fetchVatTypesAsync },
    'portal.currency': { getItems: () => websiteStore.getLocalizedCurrencies(), loadAsync: websiteStore.fetchCurrenciesAsync },
    'portal.customerelectronicinvoiceagreementtype': { getItems: () => registryStore.getLocalizedCustomerElectronicInvoiceAgreementTypes(), loadAsync: registryStore.fetchCustomerElectronicInvoiceAgreementTypesAsync },
    'registry.customer': { getItems: () => registryStore.customers, loadAsync: registryStore.fetchCustomersAsync, editor: 'customer' },
    'registry.vehicle': { getItems: () => registryStore.vehicles, loadAsync: registryStore.fetchVehiclesAsync, editor: 'vehicle' },
    'registry.deposit': { getItems: registryStore.getLocalizedDeposits, loadAsync: registryStore.fetchDepositsAsync },
    'portal.garagereason': { getItems: garageStore.getLocalizedGarageReasons, loadAsync: garageStore.fetchGarageReasonsAsync },
    'garage.documentprogressstatus': { getItems: () => garageStore.getLocalizedDocumentProgressStatuses().filter(status => status.visible && status.type !== portalStore.documentProgressStatusesEnum.PLANNED), loadAsync: garageStore.fetchDocumentProgressStatusesAsync },
    'portal.vehicletype': { getItems: () => portalStore.getLocalizedVehicleTypes(), loadAsync: portalStore.fetchVehicleTypesAsync },
    'portal.suppliertype': { getItems: () => registryStore.supplierTypes, loadAsync: registryStore.fetchSupplierTypesAsync },
    'portal.supplierbrand': { getItems: () => registryStore.supplierBrands, loadAsync: registryStore.fetchSupplierBrandsAsync },
    'portal.financialcenter': { getItems: accountingStore.getLocalizedFinancialCenters, loadAsync: accountingStore.fetchFinancialCentersAsync },
    'portal.tradeaccount': { getItems: accountingStore.getLocalizedTradeAccounts, loadAsync: accountingStore.fetchTradeAccountsAsync },
  }

  const loadFieldLookupsAsync = async(fields) => {
    const models = new Set(fields.map(field => field.lookup_model).filter(Boolean))
    await Promise.all([...models].map(model => lookupModels[model]?.loadAsync?.()))
  }

  const DATA_TYPE_TO_EDITOR = {
    string: 'text',
    text: 'textarea',
    integer: 'number',
    decimal: 'number',
    date: 'date',
    datetime: 'datetime',
    boolean: 'boolean',
  }

  const getEditorOptionsForField = (fieldDef) => {
    if (!fieldDef) return null

    if (fieldDef.transcoding_kind === 'lookup') {
      const lookup = lookupModels[fieldDef.lookup_model]
      const dataSource = lookup?.getItems?.() || []
      return { type: 'lookup', editor: lookup?.editor, dataSource, valueExpr: 'id', displayExpr: fieldDef.lookup_model === 'registry.customer' ? customerDisplayExpr : codeNameDisplayExpr }
    }
    if (fieldDef.transcoding_kind === 'choice') {
      return { type: 'lookup', dataSource: fieldDef.choices || [], valueExpr: 'value', displayExpr: 'label' }
    }
    if (fieldDef.data_type === 'boolean') {
      return { type: 'boolean' }
    }

    return { type: DATA_TYPE_TO_EDITOR[fieldDef.data_type] || 'text' }
  }

  return { getEditorOptionsForField, loadFieldLookupsAsync }
}
